// src/services/wellIntelService.js
// FlowSense MOW+ — Well & Drainage Intelligence API client (backend: routes/wells.js)
import api from './api';

const unwrap = (res) => {
  const body = res?.data;
  if (body && body.success === false) throw new Error(body.message || 'Well intelligence request failed');
  return body?.data ?? body;
};

/** Every well in the register with intelligence + fleet summary: { wells, summary, generated_at }. */
export const fetchWellFleetIntel = async ({ force = false, signal } = {}) =>
  unwrap(await api.get('/wells/intel', { params: force ? { force: 1 } : undefined, signal }));

/** One well, freshly computed. */
export const fetchWellIntel = async (wellId, { signal } = {}) =>
  unwrap(await api.get(`/wells/${encodeURIComponent(wellId)}/intel`, { signal }));

/** Recompute after an inspection / work order change; the server also pushes `well_intel_update`. */
export const refreshWellIntel = async (wellId) =>
  unwrap(await api.post(`/wells/${encodeURIComponent(wellId)}/intel/refresh`));

/** Work-order draft understood by WorkOrderPage (navigate('/admin/work-orders', { state: { woDraft } })). */
export const buildWellWorkOrderDraft = (well, recommendation) => {
  const name = well?.name || `Well ${well?.id}`;
  const rec = recommendation || well?.intel?.recommendations?.[0] || null;
  const titles = {
    DISPATCH_CREW: `Flood response — ${name}`,
    CLEAR_DRAINAGE: `Clear drainage serving ${name}`,
    SCHEDULE_INSPECTION: `Inspect ${name}`,
    SCHEDULE_CLEANING: `Silt cleaning — ${name}`,
    DESILT_WELL: `Desilt ${name}`,
    REPAIR_CASING: `Casing repair — ${name}`,
    INSTALL_SENSOR: `Install water-level sensor at ${name}`,
    CHECK_SENSOR: `Sensor offline — ${name}`,
    REVIEW_ALERTS: `Review active alerts — ${name}`,
    MAP_DRAINAGE: `Survey drainage outlet for ${name}`,
  };
  const priority = { urgent: 'critical', high: 'high', medium: 'medium', low: 'low' }[rec?.priority] || 'medium';
  const drivers = (well?.intel?.drivers || []).slice(0, 4).map((d) => `• ${d.label}: ${d.evidence}`).join('\n');
  return {
    title: titles[rec?.code] || `Well follow-up — ${name}`,
    priority,
    asset_id: well?.id ?? '',
    well_id: well?.id ?? '',
    sensor_id: well?.monitoring?.primary_device_id || '',
    location_description: well?.location_description || name,
    inspection_source: 'flowsense_well_intel',
    description: [
      rec?.text,
      well?.intel?.summary,
      drivers && `Risk drivers (FlowSense ${well?.intel?.model_version || 'well-intel'}):\n${drivers}`,
    ].filter(Boolean).join('\n\n'),
  };
};
